/**
 * WHICH INSTANCE THIS APP IS TALKING TO, for telemetry.
 *
 * Same file as apps/physio-app/src/monitoring/instanceSource.ts, restated
 * here rather than shared: the reasoning that file's own header gives for
 * staying app-local ("the right time to hoist it is when both ladders are on
 * main and one change touches one file") applies just as much to a third
 * consumer as a second.
 *
 * THE SOURCE IS THE SERVER: this module reads `x-an-instance`
 * (INSTANCE-SEPARATION R1.3), which the API puts on every response, success
 * or error — never a build flag or a hostname guess.
 *
 * `null` UNTIL THE FIRST RESPONSE, reported as `unresolved` rather than
 * invented — a crash filed before the app has ever reached the API (e.g. the
 * very first resolveLink call failing) is still a real, useful report.
 */

import { isInstance, type Instance } from '@advaitnetra/shared';

/** R1.3's header. A literal here for the same reason apps/physio-app gives. */
export const INSTANCE_HEADER = 'x-an-instance';

/** What telemetry says when the server has not answered yet. */
export const UNRESOLVED_INSTANCE = 'unresolved';

let observed: Instance | null = null;

/**
 * Record the instance the API declared on a response. Unknown values are
 * ignored — a proxy or an error page inventing a header is not the API.
 */
export function noteInstanceHeader(value: string | null | undefined): void {
  if (value && isInstance(value)) observed = value;
}

/** The last instance the API declared, or `null` if it never has. */
export function observedInstance(): Instance | null {
  return observed;
}

/** The instance as telemetry states it — never null, never invented. */
export function telemetryInstance(): string {
  return observed ?? UNRESOLVED_INSTANCE;
}

/** Tests only — module state is process-global by design. */
export function resetInstanceSourceForTest(): void {
  observed = null;
}
